import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  Building2, 
  Target,
  TrendingUp,
  Loader2,
  LogOut,
  ClipboardList,
  Clock,
  CheckCircle2,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRY_NAMES = {
  healthcare: 'Healthcare',
  it_tech: 'IT/Technology',
  finance: 'Finance',
  education: 'Education',
  retail: 'Retail'
};

const CHART_DOMAIN = [0, 100];
const CHART_TOOLTIP_STYLE = { borderRadius: '12px', border: '1px solid #E7E5E4' };
const CHART_DOT = { fill: '#2563EB' };

const EmployeeDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [myResults, setMyResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      
      const orgRes = await fetch(`${API}/corporate/organization`, { headers, credentials: 'include' });
      if (orgRes.ok) setOrganization(await orgRes.json());
    } catch (error) {
      logger.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const getInitials = (name) => {
    return name?.split(' ').map((n) => n[0]).join('').toUpperCase() || 'U';
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Excellent': return 'text-[#2563EB] bg-white/5';
      case 'Very Good': return 'text-primary bg-primary/10';
      case 'Good': return 'text-amber-600 bg-amber-100';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(210,40%,98%)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  const latestResult = myResults[0];
  const pendingAssignments = assignments.filter(a => a.status === 'assigned');
  
  const chartData = myResults.slice().reverse().map((r, i) => ({
    name: `Test ${i + 1}`,
    score: r.percentage
  }));

  return (
    <div className="min-h-screen bg-[hsl(210,40%,98%)]">
      {/* Header */}
      <header className="bg-[hsl(222,47%,4%)] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2.5" data-testid="employee-logo">
              <div className="w-9 h-9 rounded-lg bg-[hsl(217,91%,60%)] flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <span className="font-semibold text-white">{organization?.name}</span>
                <span className="text-xs text-[hsl(25,10%,55%)] block">
                  {INDUSTRY_NAMES[organization?.industry] || organization?.industry}
                </span>
              </div>
            </Link>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="rounded-full p-1" data-testid="user-menu-btn">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl">
              <div className="px-3 py-2">
                <p className="font-medium">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <p className="text-xs text-muted-foreground mt-1">{user?.department || 'General'}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer">
                <LogOut className="h-4 w-4 mr-2" />
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8" data-testid="employee-dashboard">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Welcome, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground">
            Develop your emotional intelligence with industry-specific assessments
          </p>
        </div>

        {/* Stats & Score */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Current Score */}
          <Card className="rounded-2xl border-white/10  md:col-span-2" data-testid="current-score">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Your EI Score</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-foreground">
                      {latestResult?.percentage?.toFixed(0) || '--'}
                    </span>
                    <span className="text-lg text-muted-foreground">/100</span>
                  </div>
                </div>
                {latestResult?.level && (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(latestResult.level)}`}>
                    {latestResult.level}
                  </span>
                )}
              </div>

              {latestResult?.category_scores && (
                <div className="space-y-3">
                  {Object.entries(latestResult.category_scores).slice(0, 4).map(([category, score]) => (
                    <div key={category}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="capitalize">{category.replace('_', ' ')}</span>
                        <span className="text-muted-foreground">{score}%</span>
                      </div>
                      <Progress value={score} className="h-2" />
                    </div>
                  ))}
                </div>
              )}

              {!latestResult && (
                <div className="text-center py-6">
                  <p className="text-muted-foreground mb-4">No assessment completed yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <div className="space-y-6">
            <Card className="rounded-2xl border-white/10 " data-testid="assessments-completed">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Target className="h-6 w-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{myResults.length}</p>
                    <p className="text-sm text-muted-foreground">Assessments Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-white/10 " data-testid="pending-count">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
                    <ClipboardList className="h-6 w-6 text-amber-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{pendingAssignments.length}</p>
                    <p className="text-sm text-muted-foreground">Pending Assignments</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Progress Chart & Available Assessments */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Progress Chart */}
          <Card className="rounded-2xl border-white/10  lg:col-span-2" data-testid="progress-chart">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Your Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="name" stroke="#78716C" fontSize={12} />
                      <YAxis domain={CHART_DOMAIN} stroke="#78716C" fontSize={12} />
                      <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                      <Line type="monotone" dataKey="score" stroke="#2563EB" strokeWidth={2} dot={CHART_DOT} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  Complete assessments to see your progress
                </div>
              )}
            </CardContent>
          </Card>

        </div>

        {/* Assigned Assessments */}
        {assignments.length > 0 && (
          <Card className="rounded-2xl border-white/10  mb-8" data-testid="assigned-assessments">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-primary" />
                Assigned Assessments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {assignments.map((assignment) => (
                  <div 
                    key={assignment.assignment_id}
                    className="flex items-center justify-between p-4 rounded-2xl bg-muted/50"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        assignment.status === 'completed' ? 'bg-white/5' : 'bg-amber-100'
                      }`}>
                        {assignment.status === 'completed' ? (
                          <CheckCircle2 className="h-5 w-5 text-[#2563EB]" />
                        ) : (
                          <Clock className="h-5 w-5 text-amber-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium capitalize">
                          {assignment.assessment_type === 'leadership' 
                            ? `${INDUSTRY_NAMES[assignment.industry]} Leadership` 
                            : 'General EI'} Assessment
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Assigned {new Date(assignment.assigned_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-white/5 text-[#2563EB]">
                      {assignment.status === 'completed' ? 'Completed' : 'Archived'}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Results */}
        {myResults.length > 0 && (
          <Card className="rounded-2xl border-white/10 " data-testid="recent-results">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                Recent Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {myResults.slice(0, 5).map((result, index) => (
                  <div 
                    key={result.result_id}
                    className="flex items-center justify-between p-4 rounded-2xl bg-muted/50"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <span className="font-bold text-primary">{result.percentage.toFixed(0)}</span>
                      </div>
                      <div>
                        <p className="font-medium capitalize">
                          {result.assessment_type === 'leadership' 
                            ? `${INDUSTRY_NAMES[result.industry]} Leadership` 
                            : 'General EI'} Assessment
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(result.completed_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(result.level)}`}>
                      {result.level}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recommendations */}
        {latestResult?.recommendations?.length > 0 && (
          <Card className="rounded-2xl border-white/10  mt-8" data-testid="recommendations">
            <CardHeader>
              <CardTitle>Personalized Recommendations</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {latestResult.recommendations.map((rec, index) => (
                  <li key={`rec-${rec.slice(0, 20)}`} className="flex items-start gap-3 p-4 rounded-2xl bg-muted/50">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-medium text-primary">{index + 1}</span>
                    </div>
                    <p className="text-foreground">{rec}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default EmployeeDashboard;
